import React, { createContext, useContext, useEffect, useState } from 'react';
import { API_URL } from '../config/env';

interface GrievanceContextType {
  totalGrievance: string; // existing API for compatibility
  totalGrievances: number;
  pendingGrievances: number;
  inProgressGrievances: number;
  resolvedGrievances: number;
  todaySubmissions: number;
  totalUsers: number;
  resolutionRate: number;
  lowPriorityCount: number;
  mediumPriorityCount: number;
  highPriorityCount: number;
  averageResolutionDays: number;
}

const GrievanceContext = createContext<GrievanceContextType | undefined>(undefined);

export const GrievanceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [totalGrievance, setTotalGrievance] = useState('0');
  const [totalGrievances, setTotalGrievances] = useState(0);
  const [pendingGrievances, setPendingGrievances] = useState(0);
  const [inProgressGrievances, setInProgressGrievances] = useState(0);
  const [resolvedGrievances, setResolvedGrievances] = useState(0);
  const [todaySubmissions, setTodaySubmissions] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [resolutionRate, setResolutionRate] = useState(0);
  const [lowPriorityCount, setLowPriorityCount ] = useState(0);
  const [mediumPriorityCount, setMediumPriorityCount ] = useState(0);
  const [highPriorityCount, setHighPriorityCount ] = useState(0);
  const [averageResolutionDays, setAverageResolutionDays] = useState(0);


  useEffect(() => {
    // Total (fast path)
    fetch(`${API_URL}/grievances/total`)
      .then(res => res.json())
      .then(data => setTotalGrievance(Number(data.total || 0).toLocaleString()))
      .catch(() => {});

    // Full list to compute breakdowns
    fetch(`${API_URL}/grievances`)
      .then(res => res.json())
      .then((list) => {
        const items = Array.isArray(list) ? list : [];
        setTotalGrievances(items.length);
        const today = new Date();
        today.setHours(0,0,0,0);
        let p = 0, ip = 0, r = 0, t = 0;
        for (const g of items) {
          const status = String(g.status || '').toLowerCase();
          if (status === 'pending') p++;
          else if (status === 'in-progress') ip++;
          else if (status === 'resolved') r++;
          const created = new Date(g.createdAt || g.submittedAt || 0);
          if (!isNaN(created.getTime())) {
            const cd = new Date(created);
            cd.setHours(0,0,0,0);
            if (cd.getTime() === today.getTime()) t++;
          }
        }
        setPendingGrievances(p);
        setInProgressGrievances(ip);
        setResolvedGrievances(r);
        setTodaySubmissions(t);

        // Calculate resolution rate safely
        const rate = items.length > 0 ? (r / items.length) * 100 : 0;
        setResolutionRate(rate);

        //priority calculated
        const lowPriorityCount = items.filter(g => g.priority?.toUpperCase() === 'LOW').length;
        const mediumPriorityCount = items.filter(g => g.priority?.toUpperCase() === 'MEDIUM').length;
        const highPriorityCount = items.filter(g => g.priority?.toUpperCase() === 'HIGH').length;
        setLowPriorityCount(lowPriorityCount);
        setMediumPriorityCount(mediumPriorityCount);
        setHighPriorityCount(highPriorityCount);

        // ✅ Average resolution time
        const resolvedItems = items.filter(g => 
          String(g.status || '').toLowerCase() === 'resolved' && g.createdAt && g.resolvedAt
        );

        let totalDays = 0;
        for (const g of resolvedItems) {
          const created = new Date(g.createdAt);
          const resolved = new Date(g.resolvedAt);
          if (!isNaN(created.getTime()) && !isNaN(resolved.getTime())) {
            const diffTime = resolved.getTime() - created.getTime(); // in ms
            const diffDays = diffTime / (1000 * 60 * 60 * 24);
            totalDays += diffDays;
          }
        }
        const average = resolvedItems.length > 0 ? (totalDays / resolvedItems.length) : 0;
        setAverageResolutionDays(Number(average.toFixed(1))); // round to 1 decimal

       
      })
      .catch(() => {
        setTotalGrievances(0);
        setPendingGrievances(0);
        setInProgressGrievances(0);
        setResolvedGrievances(0);
        setTodaySubmissions(0);
        setLowPriorityCount(0);
        setMediumPriorityCount(0);
        setHighPriorityCount(0);
        setAverageResolutionDays(0);

      });

    

    // Users count
    const tryUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/auth/getRegisterAdmin`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (res.ok) {
          const data = await res.json();
          setTotalUsers(Array.isArray(data) ? data.length : (Array.isArray(data?.data) ? data.data.length : 0));
          return;
        }
      } catch {}
      setTotalUsers(0);
    };
    tryUsers();
  }, []);

  return (
    <GrievanceContext.Provider value={{ 
      totalGrievance,
      totalGrievances,
      pendingGrievances,
      inProgressGrievances,
      resolvedGrievances,
      todaySubmissions,
      totalUsers,
      resolutionRate,
      lowPriorityCount,
      mediumPriorityCount,
      highPriorityCount,
      averageResolutionDays,

    }}>
      {children}
    </GrievanceContext.Provider>
  );
};

export const useGrievance = (): GrievanceContextType => {
  const context = useContext(GrievanceContext);
  if (!context) {
    throw new Error('useGrievance must be used within a GrievanceProvider');
  }
  return context;
};
